import { InjectionToken } from '@angular/core';

/** O `cardapio.json` como o CDN o entrega: o hash que o app compara e o cardápio em si. */
export interface EnvelopeDoCardapio {
    hash: string;
    cardapio: any;
}

/** O cardápio que quem renderiza já baixou, e de qual loja ele é. */
export interface CardapioDoRender {
    slug: string;
    envelope: EnvelopeDoCardapio;
}

/**
 * O cardápio entregue pronto ao render do servidor, em vez de o app baixá-lo sozinho.
 *
 * Quem provê é o `renderizarCardapio()` do `main.server.ts`, chamado pela Lambda de render e pelo
 * render de fumaça do deploy. A Lambda grava na página o ETag do `cardapio.json` que baixou, e o
 * porteiro compara esse ETag com o do S3 a cada visita para saber se a página ainda vale. Se o app
 * baixasse o cardápio de novo, uma gravação entre os dois downloads deixaria a página com o
 * conteúdo novo e o ETag velho, ou o contrário. Com o cardápio injetado, os dois vêm do mesmo
 * download.
 */
export const CARDAPIO_DO_RENDER = new InjectionToken<CardapioDoRender>('CARDAPIO_DO_RENDER');

/**
 * Campos do `cardapio.info` que NÃO vão para o estado da página do SSR.
 *
 * Nenhuma tela do app lê estes campos (conferido em 23/09/2026; o `check:campos-fora-do-estado`
 * garante que continue assim). Eles existem no `cardapio.json` para outros sistemas — o robô de
 * WhatsApp lê as mensagens dele de lá —, mas embutidos em cada página só pesavam: 1,3 a 8,5 KB
 * comprimidos por loja (2 a 13% do HTML; o mapa de áreas de entrega é o maior). E dois deles não
 * precisam viajar em página pública: a configuração de nota fiscal traz o e-mail do contador.
 *
 * O app sem SSR continua recebendo o `cardapio.json` inteiro, como sempre.
 */
export const CAMPOS_DO_INFO_FORA_DO_ESTADO: ReadonlyArray<string> = [
    'deliveryFeeZone',
    'rasaCustomMessages',
    'customStatusMessages',
    'invoice_settings',
];

/**
 * A cópia do cardápio com que o render do servidor trabalha — a original fica para a TransferState.
 *
 * As bibliotecas que montam o cardápio alteram o objeto que recebem, no lugar: a
 * `restaurantoperations` grava `info.is_closed`/`blocked`/`shift_has_*` e o `cardapnator` troca
 * `menu.general`, filtra combos, tamanhos e sabores. A TransferState só serializa no FIM do render,
 * então sem esta cópia a página levava o cardápio JÁ processado no relógio do servidor. E o
 * `is_closed` não se desfaz no navegador: a `restaurantoperations` lê o mesmo campo como
 * "fechamento manual" e devolve fechada sem olhar a hora. Foi o OP-1233 (23/09/2026): servidor em
 * UTC, lojas "fechadas" nas últimas 3 h do turno, em toda página do SSR.
 *
 * Cópia por JSON de propósito: o cardápio chega de um `cardapio.json`, então ela é exata; ~2 ms num
 * cardápio de 184 KB.
 */
export function cardapioParaORender<T>(cardapio: T): T {
    return JSON.parse(JSON.stringify(cardapio));
}

/**
 * O envelope que vai para a TransferState: o mesmo `hash` e o mesmo cardápio, sem os campos acima.
 *
 * Devolve uma CÓPIA rasa — o objeto original segue inteiro para quem renderiza. O `hash` fica o
 * mesmo de propósito: ele identifica a VERSÃO do cardápio (a checagem de frescor e o porteiro
 * comparam versão), e o cardápio embutido é essa versão menos campos que ninguém lê.
 */
export function envelopeParaOEstado(envelope: EnvelopeDoCardapio): EnvelopeDoCardapio {
    const info = envelope && envelope.cardapio && envelope.cardapio.info;
    if (!info) return envelope;
    const infoEnxuto = { ...info };
    for (const campo of CAMPOS_DO_INFO_FORA_DO_ESTADO) delete infoEnxuto[campo];
    return { ...envelope, cardapio: { ...envelope.cardapio, info: infoEnxuto } };
}
